import { ArrowLeft, Droplets, Building2, Sparkles, Layers, Lightbulb, Shield, Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import '../styles/futuristic.css';
import { useState, useEffect } from 'react';
import lumicleanLogo from '../../logos/lumiclean-bluenew.jpeg';

interface LumiCleanProps {
  onNavigate: (page: 'landing' | 'tech' | 'clean' | 'learn') => void;
  onToggleTheme: () => void;
  isDarkMode: boolean;
}

function LumiClean({ onNavigate, onToggleTheme, isDarkMode }: LumiCleanProps) {
  const [formData, setFormData] = useState({ name: '', contact: '', message: '' });
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'LumiClean | Window & Commercial Cleaning | Fort Wayne, IN';
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `LumiClean Inquiry from ${formData.name}`;
    const body = `Name: ${formData.name}\nContact: ${formData.contact}\n\nMessage:\n${formData.message}`;
    window.location.href = `mailto:portialane@lumiworksllc.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setIsSubmitted(true);
  };

  const handleCallClick = () => {
    setIsSubmitted(true);
  };

  const otherServices = [
    { icon: <Building2 className="w-8 h-8" />, title: "Business Cleaning", description: "Comprehensive cleaning services for commercial properties" },
    { icon: <Sparkles className="w-8 h-8" />, title: "Home Cleaning", description: "Thorough cleaning services for residential spaces" },
    { icon: <Layers className="w-8 h-8" />, title: "Organizing & Decluttering", description: "Professional organization and decluttering services" }
  ];

  return (
    <div className={`min-h-screen relative overflow-hidden ${isDarkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-blue-50 to-blue-100'}`}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className={`absolute w-96 h-96 rounded-full filter blur-3xl opacity-20 ${isDarkMode ? 'bg-blue-500' : 'bg-blue-300'}`} style={{ top: '10%', left: '10%' }}></div>
          <div className={`absolute w-80 h-80 rounded-full filter blur-3xl opacity-20 ${isDarkMode ? 'bg-sky-500' : 'bg-sky-300'}`} style={{ bottom: '10%', right: '10%' }}></div>
        </div>
        <div className="absolute inset-0 grid-overlay"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 py-12">
        {/* Navigation */}
        <motion.div 
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex justify-between items-center mb-12 glass-panel p-4 rounded-2xl"
        >
          <motion.button
            whileHover={{ x: -5 }}
            onClick={() => onNavigate('landing')}
            className={`flex items-center font-medium ${isDarkMode ? 'text-blue-300 hover:text-white' : 'text-blue-600 hover:text-blue-800'} transition-colors`}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </motion.button>
          <div className="flex items-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={onToggleTheme}
              className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'} shadow-md transition-all`}
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <Lightbulb className="w-5 h-5 text-yellow-300" />
              ) : (
                <Lightbulb className="w-5 h-5 text-gray-700" />
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-16"
        >
          <motion.h1 
            className={`text-5xl md:text-6xl font-bold mb-4 bg-clip-text text-transparent ${isDarkMode ? 'bg-gradient-to-r from-blue-300 to-cyan-300' : 'bg-gradient-to-r from-blue-500 to-cyan-500'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            LumiClean
          </motion.h1>
          
          <motion.p 
            className={`text-2xl font-medium mb-4 ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Professional Window Cleaning & More
          </motion.p>

          {/* Insurance Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className={`inline-flex items-center gap-3 px-6 py-3 rounded-full mb-8 ${isDarkMode ? 'bg-green-900/40 border border-green-500/50' : 'bg-green-100 border border-green-300'} shadow-lg`}
          >
            <Shield className={`w-8 h-8 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
            <span className={`text-lg font-bold ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>$1,000,000 Insured</span>
          </motion.div>

          <motion.p 
            className={`text-lg mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
          >
            Serving Fort Wayne and surrounding areas
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-wrap justify-center gap-4 mt-8"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
              className={`px-6 py-3 rounded-full font-medium flex items-center ${isDarkMode ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
            >
              Get Free Quote
              <ArrowLeft className="ml-2 w-5 h-5" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('about-section')?.scrollIntoView({ behavior: 'smooth' })}
              className={`px-6 py-3 rounded-full font-medium ${isDarkMode ? 'bg-transparent border-2 border-blue-400 text-blue-300 hover:bg-blue-900/30' : 'bg-transparent border-2 border-blue-600 text-blue-700 hover:bg-blue-100'}`}
            >
              Learn More
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Featured: Window Cleaning Section */}
        <motion.div 
          id="about-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className={`rounded-2xl p-8 mb-12 ${isDarkMode ? 'bg-gradient-to-br from-blue-900/50 to-blue-800/30 border border-blue-500/30' : 'bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200'} shadow-xl`}
        >
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-500/10'}`}>
                <Droplets className={`w-12 h-12 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`} />
              </div>
              <h2 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Window Cleaning Services</h2>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${isDarkMode ? 'bg-blue-500/30 text-blue-200' : 'bg-blue-200 text-blue-800'}`}>Our Specialty</span>
            </div>
            
            <p className={`text-lg mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              We specialize in professional window cleaning for businesses and homes. 
              Interior and exterior windows — we handle it all with care and precision.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {["Storefront windows", "Restaurants", "Offices", "Small businesses"].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className={`p-4 rounded-xl text-center ${isDarkMode ? 'bg-gray-800/60' : 'bg-white/80'} shadow-sm`}
                >
                  <p className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Location Notice */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className={`text-center p-6 rounded-xl mb-12 ${isDarkMode ? 'bg-gray-800/50' : 'bg-white/80'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
        >
          <p className={`text-lg ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <span className="font-semibold">Service Area:</span> All services including window cleaning, 
            home cleaning, business cleaning, and organizing/decluttering are available in 
            <span className={`font-bold ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}> Fort Wayne and surrounding areas</span>.
          </p>
        </motion.div>

        {/* Other Services Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h2 className={`text-2xl font-bold text-center mb-8 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Additional Services</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherServices.map((service, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
                className={`glass-panel p-6 rounded-2xl h-full flex flex-col ${isDarkMode ? 'bg-gray-800/80' : 'bg-white/90'}`}
              >
                <div className={`p-3 rounded-lg inline-flex ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'} mb-4`}>
                  {service.icon}
                </div>
                <h3 className={`text-xl font-semibold mb-2 flex-grow ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {service.title}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

          {/* Contact Section */}
          <motion.div 
            id="contact-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`rounded-2xl p-8 mb-8 ${isDarkMode ? 'bg-gray-800/80 border border-gray-700' : 'bg-white/90 border border-gray-200'} shadow-xl`}
          >
            <h2 className={`text-3xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Get in Touch</h2>
            <p className={`text-center mb-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Fill out the form below for a free quote or to learn more about our services.
            </p>

            {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Email or Phone</label>
                <input
                  type="text"
                  required
                  value={formData.contact}
                  onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  placeholder="your@email.com or (260) 555-0123"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Message</label>
                <textarea
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                  placeholder="Tell us about your cleaning needs..."
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center justify-center gap-2 px-8 py-3 rounded-lg font-semibold transition-all ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                >
                  <Send className="w-5 h-5" />
                  Send via Email
                </motion.button>
                
                <motion.a
                  href="tel:2602800434"
                  onClick={handleCallClick}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center justify-center gap-2 px-8 py-3 rounded-lg font-semibold transition-all ${isDarkMode ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                >
                  <Phone className="w-5 h-5" />
                  Call or Text (260) 280-0434
                </motion.a>
              </div>

              <div className={`text-center pt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <p className="flex items-center justify-center gap-2">
                  <Mail className="w-4 h-4" />
                  portialane@lumiworksllc.com
                </p>
              </div>
            </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, type: "spring" }}
                className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-green-900/30 border border-green-500/30' : 'bg-green-50 border border-green-200'}`}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  <CheckCircle className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                </motion.div>
                <h3 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Thank You!</h3>
                <p className={`text-lg max-w-md mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  We'll contact you back shortly. Thank you for reaching out!
                </p>
                <motion.button
                  onClick={() => setIsSubmitted(false)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`mt-6 px-6 py-2 rounded-lg font-medium transition-all ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                >
                  Send Another Message
                </motion.button>
              </motion.div>
            )}
          </motion.div>

        {/* Footer */}
        <footer className="text-center mt-12">
          <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
            &copy; {new Date().getFullYear()} LumiClean. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
}

export default LumiClean;